import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  Grid,
  Paper,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQueryClient } from '@tanstack/react-query';
import TimeRangeToggle from '../../components/dashboard/TimeRangeToggle';
import { EngagementCampaignFilterProvider } from '../../contexts/EngagementCampaignFilterContext';
import type { EngagementTimeRange } from '../../utils/engagementAnalytics';
import { clearEngagementCaches } from '../../services/outreach/engagementAnalyticsService';
import EngagementPerformanceTrends from '../../components/dashboard/EngagementPerformanceTrends';
import EngagementMetricsOverview from '../../components/dashboard/EngagementMetricsOverview';
import CampaignDistributionByTag from '../../components/dashboard/CampaignDistributionByTag';
import DeviceClientBreakdown from '../../components/dashboard/DeviceClientBreakdown';
import MostEngagedRecipients from '../../components/dashboard/MostEngagedRecipients';
import TopPerformingCampaigns from '../../components/dashboard/TopPerformingCampaigns';
import EngagementHeatmap from '../../components/dashboard/EngagementHeatmap';
import AnalyticsSummary from '../dashboard/AnalyticsSummary';
import { metricsOverviewLayoutSx } from '../../components/dashboard/EngagementInsightsContainer';

/**
 * AnalyticsPage Component
 * 
 * Serves as the dedicated Outreach Analytics page container located at /outreach/analytics.
 * This page consolidates all analytics-related sections from the dashboard into a single,
 * focused view with global time range and refresh controls.
 * 
 * Features:
 * - Page header with title and subtitle
 * - Global time range selector (7d, 30d, 90d, 180d) applying to all sections
 * - Refresh button to invalidate caches and refetch all analytics data
 * - Analytics components rendered in PRD-specified order
 * 
 * Component Order (per PRD):
 * 1. EngagementPerformanceTrends (with campaign selector)
 * 2. OutreachCampaignDistribution
 * 3. OutreachEngagementRate
 * 4. PerformanceMetricsGrid (8 cards)
 * 5. CampaignDistributionByTag
 * 6. DeviceClientBreakdown
 * 7. MostEngagedRecipients
 * 8. TopPerformingCampaigns
 * 9. EngagementHeatmap
 */
const AnalyticsPage: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [timeRange, setTimeRange] = useState<EngagementTimeRange>('30d');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const refreshAnalytics = useCallback(async () => {
    setIsRefreshing(true);
    try {
      clearEngagementCaches();
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const [key] = query.queryKey ?? [];
          if (typeof key !== 'string') {
            return false;
          }
          return [
            'engagement',
            'ses',
            'top-performing',
            'device',
            'campaign',
            'geographic',
            'recipients',
            'heatmap',
          ].some((prefix) => key.startsWith(prefix));
        },
      });
      setRefreshToken((token) => token + 1);
    } catch (error) {
      console.error('Failed to refresh analytics', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient]);

  const pageHeader = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            Outreach Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive insights into campaign performance and recipient engagement
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TimeRangeToggle value={timeRange} onChange={setTimeRange} />
          <Tooltip title="Refresh analytics">
            <span>
              <IconButton
                onClick={refreshAnalytics}
                disabled={isRefreshing}
                aria-label="Refresh analytics data"
              >
                {isRefreshing ? <CircularProgress size={22} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    ),
    [timeRange, isRefreshing, refreshAnalytics]
  );

  return (
    <EngagementCampaignFilterProvider>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {pageHeader}

        <Grid container spacing={3}>
          {/* 1. Engagement Performance Trends (with campaign selector) */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
              }}
            >
              <EngagementPerformanceTrends
                timeRange={timeRange}
                onTimeRangeChange={setTimeRange}
                refreshToken={refreshToken}
                onRefresh={refreshAnalytics}
              />
            </Paper>
          </Grid>

          {/* 2 & 3. Outreach Campaign Distribution and Engagement Rate by Tag */}
          <Grid item xs={12}>
            <AnalyticsSummary timeRange={timeRange} refreshToken={refreshToken} />
          </Grid>

          {/* 4. Performance Metrics Grid (8 cards) */}
          <Grid item xs={12}>
            <EngagementMetricsOverview
              timeRange={timeRange}
              refreshToken={refreshToken}
              sx={metricsOverviewLayoutSx}
            />
          </Grid>

          {/* 5. Campaign Distribution by Tag */}
          <Grid item xs={12} md={6}>
            <CampaignDistributionByTag />
          </Grid>

          {/* 6. Device & Client Breakdown */}
          <Grid item xs={12} md={6}>
            <DeviceClientBreakdown />
          </Grid>

          {/* 7. Most Engaged Recipients */}
          <Grid item xs={12} md={6}>
            <MostEngagedRecipients />
          </Grid>

          {/* 8. Top Performing Campaigns */}
          <Grid item xs={12} md={6}>
            <TopPerformingCampaigns />
          </Grid>

          {/* 9. Engagement Heatmap */}
          <Grid item xs={12}>
            <EngagementHeatmap />
          </Grid>
        </Grid>
      </Box>
    </EngagementCampaignFilterProvider>
  );
};

export default AnalyticsPage;
